import React, { useEffect, useState } from 'react';
import { Button, Modal, Upload, message } from 'antd';
import { FileTextOutlined, InboxOutlined } from '@ant-design/icons';
import purchaseDealSheetService from '../../../../services/purchaseDealSheetService';
import {
  SUPPLIER_DEAL_SHEET_MAX_BYTES,
  apiErrMsg,
  filenameFromUrl,
} from './purchaseDealSheet.helpers';

const { Dragger } = Upload;

export default function SupplierDealSheetUploadModal({
  open,
  onClose,
  dealSheetId,
  supplierDealSheetUrl,
  onSuccess,
}) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const isReplace = Boolean(supplierDealSheetUrl);
  const currentFilename = filenameFromUrl(supplierDealSheetUrl);

  useEffect(() => {
    if (!open) {
      setSelectedFile(null);
      setSubmitting(false);
    }
  }, [open]);

  const handleBeforeUpload = (file) => {
    if (file.size > SUPPLIER_DEAL_SHEET_MAX_BYTES) {
      message.error('File size must be 2 MB or less.');
      return Upload.LIST_IGNORE;
    }
    setSelectedFile(file);
    return false;
  };

  const handleRemove = () => {
    setSelectedFile(null);
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      message.warning('Please select a file to upload.');
      return;
    }
    if (!dealSheetId) return;

    setSubmitting(true);
    try {
      const result = await purchaseDealSheetService.uploadSupplierDealSheet(dealSheetId, selectedFile);
      message.success(isReplace ? 'Supplier deal sheet replaced' : 'Supplier deal sheet uploaded');
      onSuccess?.(result);
      onClose?.();
    } catch (err) {
      message.error(apiErrMsg(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={isReplace ? 'Replace supplier deal sheet' : 'Upload supplier deal sheet'}
      open={open}
      onCancel={onClose}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={submitting}
          disabled={!selectedFile}
          onClick={handleSubmit}
        >
          {isReplace ? 'Replace file' : 'Upload file'}
        </Button>,
      ]}
    >
      <div className="flex flex-col gap-4">
        {isReplace ? (
          <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
            <p className="m-0 text-xs font-semibold uppercase tracking-wide text-gray-400">
              Current file
            </p>
            <div className="mt-2 flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <FileTextOutlined className="shrink-0 text-blue-600" />
                <span className="truncate text-sm font-medium text-gray-800">{currentFilename}</span>
              </div>
              <a
                href={supplierDealSheetUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                View
              </a>
            </div>
          </div>
        ) : null}

        <div>
          <p className="mb-2 text-sm text-gray-600">
            {isReplace
              ? 'Select a new file to replace the current supplier deal sheet.'
              : 'Upload the supplier-provided deal sheet for this purchase deal.'}
          </p>
          <Dragger
            maxCount={1}
            fileList={
              selectedFile
                ? [{ uid: '-1', name: selectedFile.name, status: 'done' }]
                : []
            }
            beforeUpload={handleBeforeUpload}
            onRemove={handleRemove}
            disabled={submitting}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click or drag file to upload</p>
            <p className="ant-upload-hint">Maximum file size: 2 MB</p>
          </Dragger>
        </div>
      </div>
    </Modal>
  );
}
